import {
  AcademicCapIcon,
  CakeIcon,
  CalendarIcon,
} from '@heroicons/react/outline';
import { Link } from 'gatsby';
import * as React from 'react';
import * as paths from '../../staticPaths';
import { classNames } from '../../utils';

const useCases = [
  {
    name: 'Events & Other Occasions',
    description:
      'Plan seating layouts for any event, including conferences, banquets, and galas. Use our intuitive interface to arrange seats, tables, and even stages. Ensure every guest has the best seat in the house and streamline your event setup.',
    href: paths.eventLanding,
    icon: CalendarIcon,
    anchorText: 'Learn about event seating chart planning',
  },
  {
    name: (
      <>
        Classrooms:
        <br /> Schools to Universities
      </>
    ),
    description:
      'Effortlessly create detailed seating charts for school, college, university classrooms, and more. Perfect for teachers looking to optimize student placement and enhance classroom management. Tailor your layout to match room specifics and student needs.',
    icon: AcademicCapIcon,
    anchorText: 'Explore classroom seating chart maker',
    highlight: true,
  },
  {
    name: 'Weddings',
    description:
      'Design your wedding reception seating arrangement with ease. Our tool helps you visualize the placement of tables and ensure that guests are seated in the most appropriate spots. Perfect for both small family events and large, elaborate weddings.',
    href: paths.weddingLanding,
    icon: CakeIcon,
    anchorText: 'Discover wedding seating chart solutions',
  },
];
type UseCaseData = (typeof useCases)[number];

const UseCaseContent: React.FC<{ useCase: UseCaseData }> = ({ useCase }) => (
  <>
    <div className="flex items-end max-h-7">
      <h3 className="flex items-end gap-x-3 text-xl font-semibold leading-7">
        <useCase.icon
          className="h-6 w-6 my-1 flex-none text-indigo-600"
          aria-hidden="true"
        />
        {useCase.name}
      </h3>
    </div>
    <div className="mt-4 flex flex-auto flex-col text-base leading-7">
      <p className="flex-auto">{useCase.description}</p>
      {useCase.href && (
        <p className="mt-6 text-sm font-semibold leading-6 text-indigo-600 group-hover:text-indigo-800">
          {useCase.anchorText} <span aria-hidden="true">→</span>
        </p>
      )}
    </div>
  </>
);

const UseCase: React.FC<{ useCase: UseCaseData }> = ({ useCase }) => {
  const content = <UseCaseContent useCase={useCase} />;

  return useCase.href ? (
    <Link
      to={useCase.href}
      className="group cursor-pointer flex flex-col hover:text-gray-900"
    >
      {content}
    </Link>
  ) : (
    <div className="flex flex-col">{content}</div>
  );
};

export default function UseCases() {
  return (
    <div className="relative isolate overflow-hidden bg-white py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">Use cases</h2>
        </div>
        <div className="mt-12 mx-auto max-w-2xl lg:max-w-none">
          <div className="grid max-w-xl grid-cols-1 gap-x-2 gap-y-10 lg:max-w-none lg:grid-cols-3">
            {useCases.map((useCase) => (
              <div
                key={useCase.description}
                className={classNames(
                  'flex flex-col p-6',
                  useCase.highlight ? 'text-gray-900' : 'text-gray-700'
                )}
              >
                <UseCase useCase={useCase} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <svg
        viewBox="0 0 1024 1024"
        className="absolute left-3/4 top-1/4 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 -translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
        aria-hidden="true"
      >
        <circle
          cx={512}
          cy={512}
          r={512}
          fill="url(#gradient-radial-custom)"
          fillOpacity="0.2"
        />
        <defs>
          <radialGradient id="gradient-radial-custom">
            <stop stopColor="#7775D6" />
            <stop offset={1} stopColor="#E935C1" />
          </radialGradient>
        </defs>
      </svg>
      <svg
        viewBox="0 0 1024 1024"
        className="absolute left-1/3 top-3/4 -z-10 h-[64rem] w-[64rem] -translate-x-1/2 -translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)]"
        aria-hidden="true"
      >
        <circle
          cx={512}
          cy={512}
          r={512}
          fill="url(#gradient-radial-custom)"
          fillOpacity="0.15"
        />
        <defs>
          <radialGradient id="gradient-radial-custom">
            <stop stopColor="#7775D6" />
            <stop offset={1} stopColor="#E935C1" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  );
}
